import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Camera, Phone, ChevronRight, CheckCircle2, ArrowRight } from 'lucide-react';
const OPTIONS = [{
  id: 'replace',
  label: 'Replace my existing mailbox',
  recommendation: {
    title: 'Mailboxes',
    shopLink: '/6m8/mailboxes.html',
    checkText: 'Check if your current post is reusable and note your mounting type (curbside vs wall).'
  }
}, {
  id: 'new',
  label: 'Buy a new mailbox system',
  recommendation: {
    title: 'Complete Mailbox & Post Systems',
    shopLink: '/6m8/mailboxes/complete-mailbox-post-system.html',
    checkText: 'Look for complete post systems and matching address plaques for a cohesive look.'
  }
}, {
  id: 'commercial',
  label: 'Plan a commercial/community delivery setup',
  recommendation: {
    title: 'Commercial Mailboxes & Parcel Lockers',
    shopLink: '/6m8/commercial-mailboxes-lockers.html',
    checkText: 'USPS/project requirements vary by product, delivery setup, and installation type. Confirm total door count needed before ordering.'
  }
}, {
  id: 'part',
  label: 'Find a part, number, lock, or door',
  recommendation: {
    title: 'Mailbox Parts & Accessories',
    shopLink: '/6m8/mailboxes/mailbox-replacement-parts-and-accessories.html',
    checkText: 'Find the manufacturer name on your current box to ensure part compatibility.',
    helperCue: 'Can’t find the exact part? Send photos or call so MidAtlantic can help identify the model.',
    ctas: [{
      label: 'Find parts',
      href: '/6m8/mailboxes/mailbox-replacement-parts-and-accessories.html',
      primary: true
    }, {
      label: 'Ask for part help',
      href: '/ContactUs.html',
      primary: false
    }, {
      label: 'Call 301-620-7585',
      href: 'tel:301-620-7585',
      primary: false,
      icon: 'phone'
    }]
  }
}, {
  id: 'hoa',
  label: 'Match an HOA or existing model',
  recommendation: {
    title: 'Commercial Mailboxes & Parcel Lockers',
    shopLink: '/6m8/commercial-mailboxes-lockers.html',
    checkText: 'Have your HOA guidelines ready, specifically noting required colors, finials, or numbering styles.'
  }
}];
export function GuidedBuyer() {
  const [selectedId, setSelectedId] = useState<string | null>('replace');
  const selectedOption = OPTIONS.find(opt => opt.id === selectedId);
  return <section className="py-16 lg:py-24 bg-brand-background border-b border-brand-border" data-id="element-306" data-mp="2r914evk8yatv">
      <div className="max-w-7xl mx-auto px-4" data-id="element-307" data-mp="2w0j4ziicy9eb">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-308" data-mp="2w1atk9djf6v3">
          {/* Left Column: Context */}
          <div data-id="element-309" data-mp="23jozi7vycoxd">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-brand-border text-brand-maroon text-sm font-bold tracking-wide mb-6 uppercase shadow-sm" data-id="element-310" data-mp="23j6tijjhmwc9">
              <HelpCircle size={16} data-id="element-311" data-mp="1r0l00dbhw4ob" /> Expert Guidance
            </div>
            <h2 className="text-3xl lg:text-4xl font-serif font-bold text-brand-charcoal mb-6 leading-tight" data-id="element-312" data-mp="1qz9wmelyoyxq">
              Not sure what fits? We'll help narrow it down.
            </h2>
            <p className="text-lg text-brand-muted mb-8 leading-relaxed" data-id="element-313" data-mp="1i2isdex9ads2">
              Mailboxes can look similar online. The right choice may depend on
              delivery type, USPS/project requirements, HOA rules, post
              compatibility, finish, numbering, installation, and what needs to
              match your existing property. MidAtlantic can help identify
              models, match parts, and review options before you order.
            </p>

            <div className="bg-white p-5 rounded-xl border border-brand-border mb-8" data-id="element-314" data-mp="u9iqtj87zpcm">
              <h4 className="font-bold text-brand-charcoal mb-3 text-sm uppercase tracking-wider" data-id="element-315" data-mp="pcsb4x0buvrq">
                What to have ready:
              </h4>
              <ul className="text-sm text-brand-muted space-y-2" data-id="element-316" data-mp="2pgcqgowcwe3w">
                <li className="flex items-center gap-2" data-id="element-317" data-mp="2ucjxym4ehxny">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-318" data-mp="2fop3b06u1j88" /> Photos
                  of current mailbox
                </li>
                <li className="flex items-center gap-2" data-id="element-319" data-mp="2klyzcyoq8t51">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-320" data-mp="26nr0umc1ljrr" />{' '}
                  Manufacturer/model if visible
                </li>
                <li className="flex items-center gap-2" data-id="element-321" data-mp="2aqnk8f9epjwr">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-322" data-mp="1wpuo5q3gigrj" /> HOA
                  requirement if applicable
                </li>
                <li className="flex items-center gap-2" data-id="element-323" data-mp="21oxtlbncmp01">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-324" data-mp="3r2ubk5e2wf9d" />{' '}
                  Delivery type / property type
                </li>
                <li className="flex items-center gap-2" data-id="element-325" data-mp="3w1x78z86594o">
                  <CheckCircle2 size={14} className="text-brand-gold" data-id="element-326" data-mp="1kek3oe9iqxe1" /> Install
                  ZIP/location
                </li>
              </ul>
            </div>

            <p className="text-sm font-medium text-brand-charcoal mb-4 bg-brand-goldSoft p-3 rounded-lg border border-brand-gold/30" data-id="element-327" data-mp="1fe5xl1rh9xin">
              Free diagnostic and estimating help is available for residential
              replacements, commercial projects, and hard-to-identify parts.
            </p>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-328" data-mp="1y6i7qaodi33c">
              <a href="/ContactUs.html" className="bg-brand-maroon hover:bg-brand-maroonDark text-white px-6 py-3 rounded-md font-semibold text-center transition-colors shadow-sm" data-id="element-329" data-mp="1y5ytb3xxlwu2">
                Get help choosing
              </a>
              <a href="/ContactUs.html" className="bg-white border border-brand-border hover:bg-brand-panel hover:border-brand-maroon text-brand-charcoal px-6 py-3 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm" data-id="element-330" data-mp="iqrl1foy2kpw">
                <Camera size={18} className="text-brand-maroon" data-id="element-331" data-mp="17h5l4y9z8g3w" /> Send photos
                for help
              </a>
            </div>

            <div className="mt-8 flex items-center gap-3 text-brand-muted font-medium bg-white/50 inline-flex px-4 py-2 rounded-lg border border-brand-border/50" data-id="element-332" data-mp="1chtjy64g618o">
              <Phone size={18} className="text-brand-maroon" data-id="element-333" data-mp="1md526yf2dql6" />
              <span data-id="element-334" data-mp="1qeq8u21098mx">
                Prefer to talk? Call{' '}
                <a href="tel:301-620-7585" className="text-brand-charcoal hover:text-brand-maroon transition-colors" data-id="element-335" data-mp="1qd5i6nhta9uc">
                  301-620-7585
                </a>
              </span>
            </div>
          </div>

          {/* Right Column: Interactive Selector */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-border p-6 lg:p-8" data-id="element-336" data-mp="2a2zar3scoszm">
            <h3 className="text-xl font-serif font-bold text-brand-charcoal mb-6" data-id="element-337" data-mp="3ul07fis0eu0d">
              What are you trying to do?
            </h3>

            <div className="space-y-3" data-id="element-338" data-mp="3pklupnfgqvw0">
              {OPTIONS.map(option => <button key={option.id} onClick={() => setSelectedId(option.id)} className={`w-full text-left px-5 py-4 rounded-xl border transition-all duration-200 flex items-center justify-between group ${selectedId === option.id ? 'border-brand-maroon bg-white shadow-sm' : 'border-brand-border hover:border-brand-maroon/60 hover:bg-brand-panel'}`} data-id="element-339" data-mp="3ko55epwj2bhc">
                  <span className={`font-medium ${selectedId === option.id ? 'text-brand-maroon' : 'text-brand-charcoal'}`} data-id="element-340" data-mp="7er27f61zsge">
                    {option.label}
                  </span>
                  <ChevronRight size={20} className={selectedId === option.id ? 'text-brand-maroon' : 'text-gray-400 group-hover:text-brand-maroon'} data-id="element-341" data-mp="zypnylj77z4d" />
                </button>)}
            </div>

            {/* Recommendation Panel */}
            <AnimatePresence mode="wait" data-id="element-342" data-mp="3qlgen2axopht">
              {selectedOption && <motion.div key={selectedOption.id} initial={{
              opacity: 0,
              height: 0,
              marginTop: 0
            }} animate={{
              opacity: 1,
              height: 'auto',
              marginTop: 24
            }} exit={{
              opacity: 0,
              height: 0,
              marginTop: 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-343" data-mp="3vmm9b5o5mmtz">
                  <div className="bg-brand-goldSoft rounded-xl p-6 border border-brand-gold/30" data-id="element-344" data-mp="2vgabxe8llvsx">
                    <h4 className="font-bold text-brand-charcoal mb-4 flex items-center gap-2" data-id="element-345" data-mp="2qh74xo925fib">
                      <CheckCircle2 size={18} className="text-brand-maroon" data-id="element-346" data-mp="34cromlwm21qs" />{' '}
                      Recommended Next Steps
                    </h4>

                    <div className="bg-white p-4 rounded-lg border border-brand-border/50 mb-5" data-id="element-347" data-mp="2bpf56klqya1p">
                      <p className="text-sm text-brand-muted" data-id="element-348" data-mp="27pef22pacaet">
                        <strong className="text-brand-charcoal block mb-1" data-id="element-349" data-mp="2lfzxjqatuic6">
                          What to check before ordering:
                        </strong>
                        {selectedOption.recommendation.checkText}
                      </p>
                      {/* @ts-ignore */}
                      {selectedOption.recommendation.helperCue && <p className="text-sm text-brand-charcoal mt-3 pt-3 border-t border-brand-border/50 font-medium" data-id="element-350" data-mp="1o0cb2d9xeoqb">
                          {/* @ts-ignore */}
                          {selectedOption.recommendation.helperCue}
                        </p>}
                    </div>

                    {/* @ts-ignore */}
                    {selectedOption.recommendation.ctas ? <div className="flex flex-col sm:flex-row flex-wrap gap-3" data-id="element-351" data-mp="14bzcwwywp0xs">
                        {/* @ts-ignore */}
                        {selectedOption.recommendation.ctas.map((cta: any) => <a key={cta.label} href={cta.href} className={`flex-1 text-center py-2.5 px-4 rounded font-medium text-sm transition-colors flex items-center justify-center gap-2 ${cta.primary ? 'bg-brand-maroon hover:bg-brand-maroonDark text-white shadow-sm' : 'bg-white border border-brand-border hover:bg-brand-panel hover:border-brand-maroon text-brand-charcoal'}`} data-id="element-352" data-mp="uia7e4edumki">
                            {cta.icon === 'phone' && <Phone size={14} data-id="element-353" data-mp="2s7fc2bwfrmxc" />}
                            {cta.label}
                            {cta.primary && <ArrowRight size={16} data-id="element-354" data-mp="2w9is7du1vfoy" />}
                          </a>)}
                      </div> : <div className="flex flex-col sm:flex-row gap-3" data-id="element-355" data-mp="317kiaipw8f9x">
                        <a href={selectedOption.recommendation.shopLink} className="flex-1 bg-brand-maroon hover:bg-brand-maroonDark text-white text-center py-2.5 rounded font-medium text-sm transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-356" data-mp="24je8lhe46wos">
                          Shop this category <ArrowRight size={16} data-id="element-357" data-mp="1kudllyqssx02" />
                        </a>
                        <a href="/ContactUs.html" className="flex-1 bg-white border border-brand-border hover:bg-brand-panel hover:border-brand-maroon text-brand-charcoal text-center py-2.5 rounded font-medium text-sm transition-colors" data-id="element-358" data-mp="1ks141moyconj">
                          Ask MidAtlantic to review
                        </a>
                      </div>}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}